<template>
	<section class="quest-popup">
		<QuestBox :quest="quest" :is-popup="true" />
		<div
			v-if="tutorialActive && tutorialStep === 8"
			class="button-container"
		>
			<TutorialStep :type="TutorialStepTypes.GLOW">
				<template #default>
					<AppTextButton
						class="claim-rewards-btn"
						:type="StatusTypes.PRIMARY"
						:text="translate('button.accept')"
						@click="finishTutorial"
					/>
				</template>
			</TutorialStep>
		</div>
		<AppTextButton
			v-else
			class="quest-popup-btn"
			:type="
				popupType === QuestPopupType.NEW
					? StatusTypes.PRIMARY
					: StatusTypes.SUCCESS
			"
			:text="
				popupType === QuestPopupType.NEW
					? translate('button.accept')
					: translate('button.claim')
			"
			@click="popupButtonClicked(popupType)"
		/>
	</section>
</template>

<script lang="ts" setup>
import AppTextButton from "@/components/AppTextButton.vue";
import TutorialStep from "@/components/TutorialStep.vue";
import { QuestTypes } from "@/game/entities/config/quests/quest_types";
import QuestRepository from "@/game/QuestRepository";
import SoundService from "@/game/SoundService";
import useGameTicker from "@/hooks/useGameTicker";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import router from "@/router";
import useQuestStore from "@/store/useQuestStore.ts";
import type { QuestActive } from "@/types/quest";
import { QuestPopupType } from "@/types/quest";
import { RouteNames, StatusTypes, TutorialStepTypes } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { markRaw } from "vue";
import QuestBox from "./QuestBox.vue";

const claimGuard = useRewardClaimGuard();

const socketService = markRaw(useSocketService());
const soundService = markRaw(SoundService.getInstance());
const ticker = markRaw(useGameTicker());

interface Props {
	quest: QuestActive;
	popupType?: QuestPopupType;
	onClaimRewards?: (id: number) => void;
	onCloseCb?: (type?: QuestTypes) => void;
}

const props = withDefaults(defineProps<Props>(), {
	popupType: QuestPopupType.NEW,
});

const questStore = useQuestStore();
const { tutorialActive, tutorialStep } = storeToRefs(questStore);

const emit = defineEmits(["closePopup"]);

const popupButtonClicked = (type: QuestPopupType): Promise<void> =>
	claimGuard.guard((): void => {
		soundService.playGenerickClick();

		if (type === QuestPopupType.COMPLETED) {
			if (props.onClaimRewards) {
				props.onClaimRewards(props.quest.id);
			}
		} else {
			if (props.quest.type === QuestTypes.SIDE) {
				QuestRepository.getInstance().activateSideQuest(props.quest.id);
				router.push({
					name: RouteNames.QUESTS,
					params: {
						tab: 1, // side
						showPopup: "false",
					},
				});
			}
		}

		if (props.onCloseCb) {
			props.onCloseCb(props.quest.type);
		}

		emit("closePopup");
	});

const finishTutorial = (): void => {
	questStore.stopTutorial();
	socketService.emitSaveRequest({
		stores: [EmitStores.QUESTS],
	});
	soundService.playGenerickClick();
	ticker.start();

	emit("closePopup");
};
</script>

<style lang="scss" scoped>
.quest-popup {
	padding: 0.625rem 0.25rem;
	&-btn {
		margin: 0 auto;
		margin-top: 1rem;
	}

	.button-container {
		position: relative;
		padding: 1rem;
		width: 100%;
		@include flex-row-center;
	}
}
</style>
